import React, {useEffect, useState} from 'react';
import {Button, Card, Col, Form, Row, Spinner, OverlayTrigger, Tooltip} from 'react-bootstrap';
import {AppError, Loading, ProductCard} from '../';
import ModalAddResource from "../../modals/ModalAddResource";
import {Formik} from "formik";
import _ from 'lodash'
import {useProductsQuery} from "../../queries/products";
import {useRouter} from 'next/router';
import {
    mapProductToItemLine,
    mapCartToInitialValues,
    mapCartToValidationSchemaShape,
    limitAssetConfiguration,
    mapCartToAssetTypes,
    AGREEMENT_CODE_FIELD_NAME,
    updateTotalPrice,
    calculateMinimumOrderPrice,
    checkExpiredProduct,
    checkExpiredSingleProduct

} from "../../helpers/storeHelpers";
import {
    EXPIRED_AGREEMENT, INVALID_BLACKLIST_DOMAIN,
    INVALID_DOMAIN_ERROR,
    INVALID_EMAIL_ERROR, NOT_EXISTENT_AGREEMENT
} from "../../helpers/formatAssetTypeHelper";
import {useUser} from "@auth0/nextjs-auth0/client";
import {useCheckBlackListMutation} from "../../queries/blacklist";
import {toast} from "react-hot-toast";
import {useCheckAgreementMutation, useCheckStatusAgreementMutation} from "../../queries/agreements";
import AgreementPreview from "../AgreementPreview";

const Index = ({
                   cartOrOrderForUi,
                   setCartOrOrderForUi,
                   dependenciesAreReady,
                   handleSubmit,
                   submitBtnText,
                   isUpdating = false,
                   canAddBaseProducts = true,
                   canAddAddonProducts = true,
                   canDeleteProducts = true,
                   canEditAgreementCode = true,
                   BackButton = null,
                   areFieldsRequired = true,
               }) => {
    const router = useRouter();
    const {query} = router;
    const {user} = useUser();


    const [modalAddBaseProductVisible, setModalAddBaseProductVisible] = useState(false);
    const [formikInitialValues, setFormikInitialValues] = useState(null)
    const [validationSchemaShape, setValidationSchemaShape] = useState(null)
    // const [validatorTypes, setValidatorTypes] = useState(null)
    const [assetTypes, setAssetTypes] = useState(null)
    const [step, setStep] = useState(0)
    const [price, setPrice] = useState({
        subTotalBeforeDiscount: 0,
        discount: 0,
        subTotalAfterDiscount: 0,
        tax: 0,
        total: 0,
    })
    const [minPriceReached, setMinPriceReached] = useState(false)
    const [maxAssetReached, setMaxAssetReached] = useState(false)
    const [clickedOnSubmit, setClickedOnSubmit] = useState(false)
    const [minPriceValue, setMinPriceValue] = useState(600)
    const [isExpiredCart, setIsExpiredCart] = useState(false)
    //const minPriceValue =  isTestUser(user) ? 0.5 : 600
    const {
        data: products,
        isIdle: isIdleProducts,
        isLoading: isLoadingProducts,
        isError: isErrorProducts,
        error: errorProducts,
    } = useProductsQuery();

    const blacklistCheckerMutation = useCheckBlackListMutation();
    const statusAgreementMutation = useCheckStatusAgreementMutation()
    const checkAgreementMutation = useCheckAgreementMutation()

    const readyToLoadUi = dependenciesAreReady && cartOrOrderForUi && formikInitialValues
    // const readyToLoadUi = true

    /*-- USE-EFFECTS ------------------------------------------------------------------------*/

    useEffect(() => {
        if (cartOrOrderForUi && dependenciesAreReady) {
            setFormikInitialValues(mapCartToInitialValues(cartOrOrderForUi, {includeAgreementCode: true}))
            setValidationSchemaShape(mapCartToValidationSchemaShape(cartOrOrderForUi, areFieldsRequired, {includeAgreementCode: true}))
            // setValidatorTypes(mapCartToValidatorTypes(cartOrOrderForUi))
            setAssetTypes(mapCartToAssetTypes(cartOrOrderForUi))
            setMaxAssetReached(limitAssetConfiguration(cartOrOrderForUi, false))
            setMinPriceValue(calculateMinimumOrderPrice(cartOrOrderForUi))
            setPrice(updateTotalPrice(cartOrOrderForUi))
            setIsExpiredCart(checkExpiredProduct(cartOrOrderForUi))
        }
    }, [cartOrOrderForUi, dependenciesAreReady])

    useEffect(() => {
        if ((price.subTotalBeforeDiscount + price.discount) >= minPriceValue) {
            setMinPriceReached(true)
        } else {
            setMinPriceReached(false)
        }
    }, [price])

    /*-- FUNCTIONS --------------------------------------------------------------------------*/

    const addService = (el) => {
        setModalAddBaseProductVisible(false)
        setCartOrOrderForUi(prevCart => (
            {
                ...prevCart,
                item_lines: [
                    ...prevCart.item_lines,
                    mapProductToItemLine(el)
                ]
            })
        );
    }

    const deleteItemLine = (item_line) => {
        setCartOrOrderForUi(prevCart => ({
                ...prevCart,
                item_lines: prevCart.item_lines.filter((line) => line.id !== item_line.id && line.parent_item_id !== item_line.id)
            })
        );
    }

    const addProductAddon = (product, base_item_line) => {
        if (!limitAssetConfiguration(cartOrOrderForUi, true, product.sku)) {
            let newItem = mapProductToItemLine(product, base_item_line.id)
            setCartOrOrderForUi(prevCart => {
                return {
                    ...prevCart,
                    item_lines: [
                        ...prevCart.item_lines,
                        newItem
                    ]
                };
            });
        }
    }

    const updateCart = (values) => {
        const newCartForUi = _.cloneDeep(cartOrOrderForUi)
        for (const item_line of newCartForUi.item_lines) {
            for (const asset of item_line.assets) {
                const value_id = `${ item_line.id }_${ asset.id }`
                asset.value = values[value_id]
            }
        }

        if (!_.isEqual(newCartForUi, cartOrOrderForUi)) {
            setCartOrOrderForUi(newCartForUi)
        }
    }

    const onBlurFactory = (targetId, formikProps) => {
        return (e) => {
            formikProps.handleBlur(e)
            if (e.target.id === targetId && !Object.values(formikProps.errors).find(v => v === INVALID_DOMAIN_ERROR || v === INVALID_EMAIL_ERROR)) {
                updateCart(formikProps.values)
            }
        }
    }

    if (isIdleProducts || isLoadingProducts) {
        return <Loading/>
    } else if (isErrorProducts) {
        console.error(errorProducts)
        return <AppError/>
    } else {
        return (readyToLoadUi && (
                <>
                    <Formik
                        initialValues={ formikInitialValues }
                        onSubmit={ (values, formikHelpers) => {

                            let domainEntries = Object.entries(assetTypes).filter(([fieldName, assetType]) => assetType.toLowerCase() === "domain")
                            domainEntries = domainEntries.map(([fieldName, assetType]) => [fieldName, assetType, values[fieldName].toLowerCase()])

                            const agreementCodeUpperCase = values[AGREEMENT_CODE_FIELD_NAME]?.toUpperCase()
                            formikHelpers.setValues({...values, [AGREEMENT_CODE_FIELD_NAME]: agreementCodeUpperCase})

                            const valueMap = {};

                            for (let [fieldName, assetType, assetValue] of domainEntries) {
                                if (!valueMap.hasOwnProperty(assetValue)) {
                                    valueMap[assetValue] = [fieldName]
                                } else {
                                    valueMap[assetValue].push(fieldName)
                                }
                            }

                            const blacklistCheckerMutationPromise = blacklistCheckerMutation.mutateAsync({items: Object.keys(valueMap)}, {
                                onError: () => toast.error("C'è stato un problema nel controllo dei domini inseriti")
                            })

                            const agreementCodeMutationPromise = agreementCodeUpperCase
                                ? checkAgreementMutation.mutateAsync(agreementCodeUpperCase, {  // Real promise
                                    onError: (error) => {
                                        console.log(error)
                                        toast.error("C'è stato un problema nel controllo della convenzione")
                                    }
                                })
                                : Promise.resolve({})  // Fake promise in case agreement_code is not present

                            Promise.all([blacklistCheckerMutationPromise, agreementCodeMutationPromise])
                                .then(([blacklistData, agreementData]) => {
                                    let blacklistIsValid = false
                                    let agreementCodeIsValid = false
                                    const invalidDomainErrors = {}
                                    const invalidAgreementCodeErrors = {}

                                    // Check blacklist
                                    if (!blacklistData.hasOwnProperty("blacklisted")) {
                                        toast.error("C'è stato un problema")
                                        throw new Error("Should have blacklisted in the response of this call")
                                    } else if (blacklistData.blacklisted.length > 0) {
                                        for (let invalidDomain of blacklistData?.blacklisted ?? []) {
                                            for (let invalidFieldName of valueMap[invalidDomain]) {
                                                invalidDomainErrors[invalidFieldName] = INVALID_BLACKLIST_DOMAIN
                                            }
                                        }
                                    } else {
                                        blacklistIsValid = true
                                    }

                                    // Check agreement code
                                    if (!agreementCodeUpperCase             // if agreement code is not present
                                        || (agreementData.status === 200 && agreementData.data.enabled)) {  // or if agreement code is valid
                                        agreementCodeIsValid = true         // mark agreement code validation as valid
                                    } else { // only non-200 status code permitted here is 404
                                        // so this is 404 (see mutator configuration)
                                        if (!agreementData?.data?.enabled) {
                                            invalidAgreementCodeErrors[AGREEMENT_CODE_FIELD_NAME] = EXPIRED_AGREEMENT
                                        } else {
                                            invalidAgreementCodeErrors[AGREEMENT_CODE_FIELD_NAME] = NOT_EXISTENT_AGREEMENT
                                        }
                                    }
                                    
                                    // Eventually submit
                                    if (blacklistIsValid && agreementCodeIsValid) {
                                        formikHelpers.setErrors({})
                                        handleSubmit(values, formikHelpers)
                                    } else {
                                        formikHelpers.setErrors({...invalidDomainErrors, ...invalidAgreementCodeErrors})
                                    }
                                })
                                .catch(values => {
                                    console.error(values)
                                })
                        } }
                        validationSchema={ () => validationSchemaShape }
                    >{ (formikProps) => (
                        <Form noValidate onSubmit={ (e) => {
                            // Work-around for dynamic form where onSubmit formik resets all touched and only sets to true
                            // the ones in initialField at time of loading. (error messages were not shown for new fields)
                            // We didn't set enableReinitialized because it had other side effects causing other bugs
                            setClickedOnSubmit(true)
                            formikProps.handleSubmit(e)
                        } }>
                            {!cartOrOrderForUi?.agreement?.is_partnership ?
                            <Card>
                                <Row >
                                    <Col sm={ 12 }>

                                            <Card.Header >
                                            <div >
                                                <h4 className="card-header-title">Codice convenzione</h4>
                                                <h5 className="card-subtitle pt-3 text-muted">{
                                                    canEditAgreementCode
                                                        ? 'Se sei in possesso del codice convenzione, inseriscilo nel campo sottostante.'
                                                        : 'Ordine scontato grazie alla convenzione applicata'
                                                }</h5>
                                            </div>
                                        </Card.Header>
                                        <Card.Body className='pb-4'>
                                            <Row className='mb-1'>
                                                { cartOrOrderForUi?.agreement?.logo
                                                    && <Col sm={ 2 } className="form-group my-auto">
                                                        <img
                                                            src={ 'data:image/png;base64,' + cartOrOrderForUi?.agreement?.logo }
                                                            alt='agreement-logo'
                                                            style={ {maxWidth: '100%', maxHeight: '60px'} }/>
                                                    </Col> }
                                                <Col sm={ 10 } className="form-group my-auto">

                                                    <Form.Control
                                                        // #TODO da completare appena ho il backend
                                                        name={ AGREEMENT_CODE_FIELD_NAME }
                                                        disabled={ !canEditAgreementCode }
                                                        type={ 'text' }
                                                        placeholder={ 'es:AS1234' }
                                                        value={ formikProps.values[AGREEMENT_CODE_FIELD_NAME] }
                                                        onChange={ (e) => {
                                                            e.target.value = e.target.value?.toUpperCase()
                                                            formikProps.handleChange(e)
                                                        } }
                                                        onBlur={ formikProps.handleBlur }
                                                        onKeyDown={ e => {
                                                            e.key === 'Enter' && e.preventDefault()
                                                        } }
                                                        isInvalid={ (formikProps.touched[AGREEMENT_CODE_FIELD_NAME] || clickedOnSubmit) && !!formikProps.errors[AGREEMENT_CODE_FIELD_NAME] }/>
                                                    <Form.Control.Feedback
                                                        type="invalid">{ formikProps.errors[AGREEMENT_CODE_FIELD_NAME] }</Form.Control.Feedback>
                                                </Col>
                                            </Row>
                                        </Card.Body>
                                    </Col>
                                </Row>
                            </Card>:<AgreementPreview cart={ cartOrOrderForUi }/>}
                            {
                                cartOrOrderForUi.item_lines
                                    .filter(item_line => item_line.parent_item_id == null)
                                    .map(item_line =>
                                        <ProductCard className='mb-0'
                                                     base_item_line={ item_line }
                                                     item_lines={ cartOrOrderForUi.item_lines }
                                                     formikProps={ formikProps }
                                                     key={ `card_${ item_line.id }` }
                                                     onDeleteItem={ deleteItemLine }
                                                     onBlurFactory={ onBlurFactory }
                                                     addResource={ addProductAddon }
                                                     products={ products }
                                                     isExpired={ checkExpiredSingleProduct(item_line) }
                                                     canAddAddonProducts={ canAddAddonProducts }
                                                     canDeleteProducts={ canDeleteProducts }
                                                     clickedOnSubmit={ clickedOnSubmit }
                                        />)
                            }
                            <div className="d-grid mb-4">
                                { (canAddBaseProducts &&
                                    (<>
                                        <Button variant="outline-secondary" size="lg"
                                                style={ {border: '1px dashed'} }
                                                onClick={ (event) => {
                                                    event.preventDefault()
                                                    setModalAddBaseProductVisible(true)
                                                } }>
                                            + Aggiungi nuovo servizio
                                        </Button>
                                        <ModalAddResource
                                            onCLickAddResource={ addService }
                                            visible={ modalAddBaseProductVisible }
                                            id_product={ null }
                                            products={ products }
                                            isPrimary={ true }
                                            onDismiss={ () => setModalAddBaseProductVisible(false) }
                                            variant="secondary"
                                        />
                                    </>)) }
                            </div>
                            {/*{ !minPriceReached &&*/}
                            {/*    <Card className="text-center">*/}
                            {/*        <Card.Body className="card-header-title">Per completare l'acquisto è necessario*/}
                            {/*            raggiungere la*/}
                            {/*            cifra di € { minPriceValue } IVA esclusa</Card.Body>*/}
                            {/*    </Card>*/}
                            {/*}*/}
                            <Card>
                                <Card.Body>
                                    <div className={'cart-price-container'}>
                                    <Row>
                                        <Col>
                                            <span>Imponibile</span>
                                            { cartOrOrderForUi.agreement && (
                                                <>
                                                    <span>Sconto { cartOrOrderForUi.agreement.name } (-{ cartOrOrderForUi.agreement.discount_percentage_ratio * 100 } %)</span>
                                                    <span>Imponibile scontato</span>
                                                </>
                                            ) }
                                            <span>22% IVA</span>
                                            <span className='p-0 m-0 price-total'>Totale</span>
                                        </Col>
                                        <Col className='text-end'>
                                            <span className={ (cartOrOrderForUi.agreement_item_line) ? 'text-decoration-line-through' : '' }>
                                                { (price.subTotalBeforeDiscount).toFixed(2) } €
                                            </span>
                                            { cartOrOrderForUi.agreement && (
                                                <>
                                                    <span>{ (-price.discount).toFixed(2) } €</span>
                                                    <span>{ (price.subTotalAfterDiscount).toFixed(2) } €</span>
                                                </>
                                            ) }
                                            <span>{ (price.tax).toFixed(2) } €</span>
                                            <span className='p-0 m-0 price-total'>{ (price.total).toFixed(2) } €</span>
                                        </Col>
                                    </Row>
                                    </div>
                                </Card.Body>
                            </Card>
                            <div className="d-sm-flex justify-content-sm-between d-grid gap-3 mb-5 mt-5">
                                { BackButton ? <BackButton/> : <div></div> }
                                <OverlayTrigger overlay={ !minPriceReached ?
                                    <Tooltip id="tooltip-disabled">Inserire almeno un articolo</Tooltip> : <></> }>
                                <span className='d-grid'>
                                    <Button size="lg" type="submit"
                                            disabled={ isUpdating || !minPriceReached || maxAssetReached || isExpiredCart }>
                                        { isUpdating ? <Spinner animation="border" size={ "sm" }/> : submitBtnText }
                                    </Button>
                                </span>
                                </OverlayTrigger>
                            </div>
                        </Form>
                    ) }
                    </Formik>
                </>
            )
        )
    }
}

export default Index